import { create, type StoreApi } from 'zustand';
import {
  type Alert,
  type AlertRule,
  type LogLine,
  type MetricSample,
  type ProcessDetail,
  type ProcessSummary,
  type SystemInfo,
} from '@pm2-monitor/shared';
import { ApiError, api } from '../api/client';
import { clearToken, readToken, writeToken, type TokenScope } from '../api/connection';
import { BridgeStream, type StreamStatus } from '../api/stream';
import { CPU_STEPS, MEMORY_STEPS, ceilingFor } from '../lib/chart';
import { toMiB } from '../lib/format';

export type View = 'overview' | 'metrics' | 'logs' | 'alerts' | 'server';
export type DrawerTab = 'overview' | 'logs' | 'env';
export type AuthState = 'unknown' | 'checking' | 'ok' | 'rejected';

/** Chart windows offered in the top bar, in seconds. */
export const RANGES = [60, 900, 3600] as const;
export type Range = (typeof RANGES)[number];

/**
 * Sample retention. The bridge keeps a 60-second in-memory window and no
 * database, so anything longer has to be accumulated here, from the status
 * frames, for as long as the tab stays open. One hour of one-second samples is
 * three parallel arrays of 3600 numbers — cheap enough to be worth the honesty
 * of showing a real 15m and 1h view instead of an empty one.
 */
const SERVER_CAPACITY = 3600;
/** Per-process history only backs the sparklines and the drawer's 60s chart. */
const PROCESS_CAPACITY = 900;
/** The design's stated buffer cap for the log feed. */
const LOG_CAPACITY = 400;

function pushCapped(list: number[], value: number, capacity: number): number[] {
  const next =
    list.length >= capacity ? list.slice(list.length - capacity + 1) : list.slice();
  next.push(value);
  return next;
}

interface State {
  token: string | null;
  auth: AuthState;
  authError: string | null;
  /** Set while the connect form is submitting, so the button can show it. */
  signingIn: boolean;

  streamStatus: StreamStatus;
  statusIntervalMs: number;
  connectedSince: number | null;
  /** Wall clock, ticked once a second so uptimes and ages stay live. */
  now: number;

  system: SystemInfo | null;
  processes: ProcessSummary[];
  alerts: Alert[];
  rules: AlertRule[];
  logs: LogLine[];

  /** Server aggregate history. Parallel arrays, oldest first. */
  sampleTimes: number[];
  sampleCpu: number[];
  /** MiB, converted once on ingest so no chart has to divide. */
  sampleMem: number[];
  /** Per-process CPU history, keyed by pm2 id. */
  processCpu: Record<number, number[]>;

  /** Ratcheted axis ceilings; see `ceilingFor`. */
  cpuCeiling: number;
  memCeiling: number;

  view: View;
  range: Range;
  selectedId: number | null;
  drawerTab: DrawerTab;
  theme: 'light' | 'dark';

  paused: boolean;
  logFilter: string | null;
  /** Process shown in the Metrics view's runtime panel. */
  runtimeId: number | null;

  detail: ProcessDetail | null;
  detailSeries: MetricSample[];
  detailLogs: LogLine[];
  detailLoading: boolean;
  detailError: string | null;

  loadError: string | null;
}

interface Actions {
  signIn: (token: string, scope: TokenScope) => Promise<void>;
  signOut: () => void;
  restore: () => Promise<void>;
  refresh: () => Promise<void>;
  retryStream: () => void;

  setView: (view: View) => void;
  setRange: (range: Range) => void;
  setTheme: (theme: 'light' | 'dark') => void;
  select: (id: number | null) => void;
  setDrawerTab: (tab: DrawerTab) => void;
  setRuntimeId: (id: number) => void;

  togglePause: () => void;
  setLogFilter: (name: string | null) => void;

  acknowledgeAlert: (id: string) => Promise<void>;
  toggleRule: (id: string) => Promise<void>;

  tick: () => void;
}

let stream: BridgeStream | null = null;
let clock: ReturnType<typeof setInterval> | null = null;

/**
 * Incoming log lines are buffered and flushed on an interval. A chatty process
 * can emit hundreds of lines a second, and turning each one into its own store
 * write would re-render the whole console that many times.
 */
let logInbox: LogLine[] = [];
let logFlush: ReturnType<typeof setInterval> | null = null;
const LOG_FLUSH_MS = 250;

function readInitialTheme(): 'light' | 'dark' {
  const attr = document.documentElement.getAttribute('data-theme');
  return attr === 'dark' ? 'dark' : 'light';
}

export const useStore = create<State & Actions>((set, get) => ({
  token: null,
  auth: 'unknown',
  authError: null,
  signingIn: false,

  streamStatus: 'closed',
  statusIntervalMs: 1000,
  connectedSince: null,
  now: Date.now(),

  system: null,
  processes: [],
  alerts: [],
  rules: [],
  logs: [],

  sampleTimes: [],
  sampleCpu: [],
  sampleMem: [],
  processCpu: {},

  cpuCeiling: CPU_STEPS[0],
  memCeiling: MEMORY_STEPS[0],

  view: 'overview',
  range: 60,
  selectedId: null,
  drawerTab: 'overview',
  theme: readInitialTheme(),

  paused: false,
  logFilter: null,
  runtimeId: null,

  detail: null,
  detailSeries: [],
  detailLogs: [],
  detailLoading: false,
  detailError: null,

  loadError: null,

  async restore() {
    const stored = readToken();
    if (!stored) {
      set({ auth: 'rejected', authError: null });
      return;
    }
    set({ token: stored.token, auth: 'checking' });
    await get().refresh();
  },

  async signIn(token, scope) {
    set({ signingIn: true, authError: null });
    try {
      // Prove the token before persisting it, so a typo is not stored and
      // then silently retried on every subsequent load.
      await api.system(token);
      writeToken(token, scope);
      set({ token, auth: 'checking', signingIn: false });
      await get().refresh();
    } catch (error) {
      const message =
        error instanceof ApiError && error.status === 401
          ? 'That token was rejected by the bridge.'
          : error instanceof Error
            ? error.message
            : 'Could not reach the bridge.';
      set({ signingIn: false, authError: message, auth: 'rejected' });
    }
  },

  signOut() {
    clearToken();
    stream?.close();
    stream = null;
    if (clock) clearInterval(clock);
    clock = null;
    if (logFlush) clearInterval(logFlush);
    logFlush = null;
    logInbox = [];
    set({
      token: null,
      auth: 'rejected',
      authError: null,
      streamStatus: 'closed',
      connectedSince: null,
      system: null,
      processes: [],
      alerts: [],
      rules: [],
      logs: [],
      sampleTimes: [],
      sampleCpu: [],
      sampleMem: [],
      processCpu: {},
      selectedId: null,
      detail: null,
      detailSeries: [],
      detailLogs: [],
    });
  },

  /**
   * Cold start: fill every panel from REST, then hand over to the stream.
   * `/metrics/history` seeds the charts with the bridge's own 60-second window
   * so the first frame is not a blank axis.
   */
  async refresh() {
    const token = get().token;
    if (!token) return;

    try {
      const [system, processes, history, alerts, logs] = await Promise.all([
        api.system(token),
        api.processes(token),
        api.metricsHistory(token),
        api.alerts(token),
        api.recentLogs(token, LOG_CAPACITY),
      ]);

      const sampleTimes = history.server.map((s) => s.timestamp);
      const sampleCpu = history.server.map((s) => s.cpu);
      const sampleMem = history.server.map((s) => toMiB(s.memory));

      const processCpu: Record<number, number[]> = {};
      for (const [id, series] of Object.entries(history.byProcess)) {
        processCpu[Number(id)] = series.map((s) => s.cpu);
      }

      const totalMemoryMiB = toMiB(system.totalMemory);

      set((state) => ({
        auth: 'ok',
        authError: null,
        loadError: null,
        system,
        processes: processes.processes,
        alerts: alerts.alerts,
        rules: alerts.rules,
        logs: logs.lines.slice(-LOG_CAPACITY),
        sampleTimes,
        sampleCpu,
        sampleMem,
        processCpu,
        runtimeId:
          state.runtimeId ??
          [...processes.processes].sort((a, b) => b.memory - a.memory)[0]?.id ??
          null,
        cpuCeiling: ceilingFor(Math.max(0, ...sampleCpu), CPU_STEPS, state.cpuCeiling),
        memCeiling: ceilingFor(
          Math.max(0, ...sampleMem),
          MEMORY_STEPS,
          state.memCeiling,
          totalMemoryMiB,
        ),
      }));

      startStream(token, set, get);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        clearToken();
        set({
          auth: 'rejected',
          token: null,
          authError: 'The stored token is no longer accepted. Sign in again.',
        });
        return;
      }
      set({
        loadError: error instanceof Error ? error.message : String(error),
        // A network failure is not an auth failure: keep the console up and
        // let the stream's own backoff recover it.
        auth: get().auth === 'checking' ? 'ok' : get().auth,
      });
      startStream(token, set, get);
    }
  },

  retryStream() {
    stream?.retryNow();
  },

  setView: (view) => set({ view }),
  setRange: (range) => set({ range }),
  setTheme(theme) {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('pm2:theme', theme);
    } catch {
      // Preference simply does not persist when storage is unavailable.
    }
    set({ theme });
  },

  select(id) {
    set({
      selectedId: id,
      drawerTab: 'overview',
      detail: null,
      detailError: null,
    });
    if (id === null) return;
    void loadDetail(id, set, get);
  },

  setDrawerTab(tab) {
    set({ drawerTab: tab });
    const { selectedId, detail } = get();
    // The file tail is only worth fetching once the Logs tab is actually
    // opened; it reads from disk on the host.
    if (tab === 'logs' && selectedId !== null && detail) {
      void loadDetailLogs(selectedId, set, get);
    }
  },

  setRuntimeId(id) {
    set({ runtimeId: id });
  },

  togglePause: () => set((s) => ({ paused: !s.paused })),
  setLogFilter: (name) => set({ logFilter: name }),

  async acknowledgeAlert(id) {
    const token = get().token;
    if (!token) return;
    // Optimistic: the bridge drops acknowledged alerts from `getActive`, so
    // waiting for the round trip would leave the card sitting there.
    set((s) => ({ alerts: s.alerts.filter((a) => a.id !== id) }));
    try {
      await api.acknowledgeAlert(token, id);
    } catch {
      const fresh = await api.alerts(token).catch(() => null);
      if (fresh) set({ alerts: fresh.alerts, rules: fresh.rules });
    }
  },

  async toggleRule(id) {
    const token = get().token;
    const rule = get().rules.find((r) => r.id === id);
    if (!token || !rule) return;
    const enabled = !rule.enabled;
    set((s) => ({
      rules: s.rules.map((r) => (r.id === id ? { ...r, enabled } : r)),
    }));
    try {
      const { rule: saved } = await api.updateRule(token, id, { enabled });
      set((s) => ({ rules: s.rules.map((r) => (r.id === id ? saved : r)) }));
    } catch {
      set((s) => ({ rules: s.rules.map((r) => (r.id === id ? rule : r)) }));
    }
  },

  tick: () => set({ now: Date.now() }),
}));

type Store = State & Actions;
type Set = StoreApi<Store>['setState'];
type Get = StoreApi<Store>['getState'];

/**
 * Attach the live stream. Status frames drive every number on screen; the same
 * tick that updates the process list appends to the metric rings, so the
 * charts and the table can never disagree.
 */
function startStream(token: string, set: Set, get: Get): void {
  stream?.close();

  stream = new BridgeStream(token, {
    onStatusChange(status, detail) {
      if (detail === 'unauthorized') {
        clearToken();
        set({
          auth: 'rejected',
          token: null,
          authError: 'The bridge rejected the token on the live stream.',
          streamStatus: 'closed',
        });
        return;
      }
      set((state) => ({
        streamStatus: status,
        connectedSince: status === 'open' ? (state.connectedSince ?? Date.now()) : null,
      }));
      if (status === 'open') {
        // Every level, every process: filtering happens client-side here
        // because the chips switch instantly and a round trip per chip would
        // drop lines during the change.
        stream?.send({
          type: 'subscribe_logs',
          payload: {
            processIds: [],
            levels: ['info', 'warn', 'error', 'debug'],
          },
        });
      }
    },

    onMessage(message) {
      switch (message.type) {
        case 'hello':
          set({ statusIntervalMs: message.payload.statusIntervalMs });
          break;

        case 'status': {
          const { processes, serverSample } = message.payload;
          set((state) => {
            const memMiB = toMiB(serverSample.memory);
            const processCpu: Record<number, number[]> = {};
            for (const proc of processes) {
              processCpu[proc.id] = pushCapped(
                state.processCpu[proc.id] ?? [],
                proc.status === 'online' ? proc.cpu : 0,
                PROCESS_CAPACITY,
              );
            }
            const sampleCpu = pushCapped(
              state.sampleCpu,
              serverSample.cpu,
              SERVER_CAPACITY,
            );
            const sampleMem = pushCapped(state.sampleMem, memMiB, SERVER_CAPACITY);
            const totalMemoryMiB = state.system
              ? toMiB(state.system.totalMemory)
              : undefined;
            return {
              processes,
              now: serverSample.timestamp,
              sampleTimes: pushCapped(
                state.sampleTimes,
                serverSample.timestamp,
                SERVER_CAPACITY,
              ),
              sampleCpu,
              sampleMem,
              // Rings for processes deleted since the last tick are dropped
              // with the rebuild above rather than leaking for the session.
              processCpu,
              cpuCeiling: ceilingFor(
                Math.max(0, ...sampleCpu),
                CPU_STEPS,
                state.cpuCeiling,
              ),
              memCeiling: ceilingFor(
                Math.max(0, ...sampleMem),
                MEMORY_STEPS,
                state.memCeiling,
                totalMemoryMiB,
              ),
            };
          });
          break;
        }

        case 'log':
          if (!get().paused) logInbox.push(message.payload);
          break;

        case 'alert':
          set((state) => ({ alerts: [message.payload, ...state.alerts] }));
          break;

        case 'action':
        case 'error':
          break;
      }
    },
  });

  stream.connect();

  if (!clock) clock = setInterval(() => get().tick(), 1000);
  if (!logFlush) {
    logFlush = setInterval(() => {
      if (logInbox.length === 0) return;
      const batch = logInbox;
      logInbox = [];
      set((state) => ({
        logs: [...state.logs, ...batch].slice(-LOG_CAPACITY),
      }));
    }, LOG_FLUSH_MS);
  }
}

async function loadDetail(id: number, set: Set, get: Get): Promise<void> {
  const token = get().token;
  if (!token) return;
  set({ detailLoading: true, detailError: null, detailLogs: [] });
  try {
    const { process, series } = await api.process(token, id);
    // The drawer may have been closed or moved on while this was in flight.
    if (get().selectedId !== id) return;
    set({ detail: process, detailSeries: series, detailLoading: false });
  } catch (error) {
    if (get().selectedId !== id) return;
    set({
      detailLoading: false,
      detailError: error instanceof Error ? error.message : String(error),
    });
  }
}

async function loadDetailLogs(id: number, set: Set, get: Get): Promise<void> {
  const token = get().token;
  if (!token) return;
  try {
    const { lines } = await api.processLogs(token, id, {
      lines: 200,
      source: 'file',
    });
    if (get().selectedId !== id) return;
    set({ detailLogs: lines });
  } catch {
    // The drawer falls back to the live buffer for this process.
  }
}
